import { type FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  confirmPasswordReset,
  fetchTrialConfig,
  requestPasswordReset,
} from "../api/auth";
import { getLoginErrorMessage, useAuth } from "../context/AuthContext";
import { LangSwitch } from "../components/layout/LangSwitch";

type AuthMode = "login" | "register" | "forgot" | "forgot_confirm";

export function LoginPage() {
  const { t } = useTranslation();
  const { login, registerAccount } = useAuth();

  const [mode, setMode] = useState<AuthMode>("login");
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [orgId, setOrgId] = useState("");

  const [companyName, setCompanyName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regWhatsapp, setRegWhatsapp] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regPasswordConfirm, setRegPasswordConfirm] = useState("");
  const [provisionKey, setProvisionKey] = useState("");
  const [requiresProvisionKey, setRequiresProvisionKey] = useState(false);

  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotWhatsappTail, setForgotWhatsappTail] = useState("");
  const [otpChannel, setOtpChannel] = useState<0 | 1>(0);
  const [otpCode, setOtpCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [devOtpHint, setDevOtpHint] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    void fetchTrialConfig()
      .then((cfg) => setRequiresProvisionKey(cfg.requiresProvisionKey))
      .catch(() => setRequiresProvisionKey(false));
  }, []);

  function resetMessages() {
    setError(null);
    setSuccess(null);
    setDevOtpHint(null);
  }

  async function handleLoginSubmit(e: FormEvent) {
    e.preventDefault();
    resetMessages();
    setSubmitting(true);
    try {
      await login(loginId, password, orgId || undefined);
    } catch (err) {
      setError(getLoginErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRegisterSubmit(e: FormEvent) {
    e.preventDefault();
    resetMessages();
    setSubmitting(true);
    try {
      await registerAccount({
        companyName: companyName.trim(),
        email: regEmail.trim().toLowerCase(),
        whatsapp: regWhatsapp.replace(/\D/g, ""),
        password: regPassword,
        confirmPassword: regPasswordConfirm,
        provisionKey: provisionKey.trim() || undefined,
      });
    } catch (err) {
      setError(getLoginErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleForgotRequest(e: FormEvent) {
    e.preventDefault();
    resetMessages();
    setSubmitting(true);
    try {
      const res = await requestPasswordReset({
        email: forgotEmail.trim().toLowerCase(),
        whatsappTail: forgotWhatsappTail.replace(/\D/g, "").slice(-4),
        channel: otpChannel,
      });
      setSuccess(res.message);
      if (res.devOtp) setDevOtpHint(`${t("auth.dev_otp_hint")}: ${res.devOtp}`);
      setMode("forgot_confirm");
    } catch (err) {
      setError(getLoginErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleForgotConfirm(e: FormEvent) {
    e.preventDefault();
    resetMessages();
    setSubmitting(true);
    try {
      const res = await confirmPasswordReset({
        email: forgotEmail.trim().toLowerCase(),
        otp: otpCode.trim(),
        newPassword,
        confirmPassword: newPasswordConfirm,
      });
      setSuccess(res.message);
      setMode("login");
    } catch (err) {
      setError(getLoginErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  const titleKey =
    mode === "register"
      ? "auth.register_title"
      : mode === "forgot" || mode === "forgot_confirm"
        ? "auth.forgot_title"
        : "auth.login_title";

  const hintKey =
    mode === "register"
      ? "auth.register_hint"
      : mode === "forgot"
        ? "auth.forgot_hint"
        : mode === "forgot_confirm"
          ? "auth.forgot_confirm_hint"
          : "auth.login_subtitle";

  return (
    <div dir="rtl" className="godo-login-screen">
      <div className="godo-login-panel">
        <LangSwitch variant="login" />

        <div className="mb-6 text-center">
          <img
            src="/godo-logo.png?v=20260604"
            alt="Go.Do ERP"
            className="godo-login-logo"
            width={280}
            height={120}
          />
          <h1 className="text-xl font-bold text-godo-primary">{t("labels.app_name")}</h1>
          <p className="mt-1 text-sm text-godo-muted-green">{t("auth.brand_unified_login")}</p>
          <h2 className="mt-4 text-xl font-bold text-godo-accent">{t(titleKey)}</h2>
          <p className="mt-1 text-sm text-godo-muted-green">{t(hintKey)}</p>
        </div>

        <form
          onSubmit={
            mode === "register"
              ? handleRegisterSubmit
              : mode === "forgot"
                ? handleForgotRequest
                : mode === "forgot_confirm"
                  ? handleForgotConfirm
                  : handleLoginSubmit
          }
          className="godo-login-form space-y-4"
        >
          {mode === "login" && (
            <>
              <Field label={t("auth.email_login")}>
                <input
                  type="email"
                  required
                  autoComplete="username"
                  value={loginId}
                  onChange={(e) => setLoginId(e.target.value)}
                  className="godo-login-input"
                />
              </Field>
              <Field label={t("auth.password")}>
                <input
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="godo-login-input"
                />
              </Field>
              <Field label={`${t("auth.org_id")} ${t("auth.org_id_optional")}`}>
                <input
                  type="text"
                  value={orgId}
                  onChange={(e) => setOrgId(e.target.value)}
                  className="godo-login-input"
                />
              </Field>
            </>
          )}

          {mode === "register" && (
            <>
              <Field label={t("auth.company_name")}>
                <input
                  type="text"
                  required
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="godo-login-input"
                />
              </Field>
              <Field label={t("auth.email")}>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={regEmail}
                  onChange={(e) => setRegEmail(e.target.value)}
                  className="godo-login-input"
                />
              </Field>
              <Field label={t("auth.whatsapp")}>
                <input
                  type="tel"
                  required
                  dir="ltr"
                  value={regWhatsapp}
                  onChange={(e) => setRegWhatsapp(e.target.value)}
                  className="godo-login-input"
                  placeholder="201012345678"
                />
              </Field>
              {requiresProvisionKey && (
                <Field label={t("auth.provision_key")}>
                  <input
                    type="text"
                    value={provisionKey}
                    onChange={(e) => setProvisionKey(e.target.value)}
                    className="godo-login-input"
                  />
                </Field>
              )}
              <Field label={t("auth.password")}>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={regPassword}
                  onChange={(e) => setRegPassword(e.target.value)}
                  className="godo-login-input"
                />
              </Field>
              <Field label={t("auth.password_confirm")}>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={regPasswordConfirm}
                  onChange={(e) => setRegPasswordConfirm(e.target.value)}
                  className="godo-login-input"
                />
              </Field>
              <p className="text-xs text-godo-muted-green">{t("auth.trial_note")}</p>
            </>
          )}

          {(mode === "forgot" || mode === "forgot_confirm") && (
            <>
              <Field label={t("auth.email")}>
                <input
                  type="email"
                  required
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="godo-login-input"
                  readOnly={mode === "forgot_confirm"}
                />
              </Field>
              {mode === "forgot" && (
                <>
                  <Field label={t("auth.whatsapp_tail")}>
                    <input
                      type="text"
                      required
                      dir="ltr"
                      maxLength={4}
                      inputMode="numeric"
                      value={forgotWhatsappTail}
                      onChange={(e) =>
                        setForgotWhatsappTail(e.target.value.replace(/\D/g, "").slice(-4))
                      }
                      className="godo-login-input"
                    />
                  </Field>
                  <fieldset className="space-y-2">
                    <legend className="text-sm font-medium text-godo-mint">
                      {t("auth.otp_channel_title")}
                    </legend>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="otpChannel"
                        checked={otpChannel === 0}
                        onChange={() => setOtpChannel(0)}
                      />
                      {t("auth.otp_channel_email")}
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="otpChannel"
                        checked={otpChannel === 1}
                        onChange={() => setOtpChannel(1)}
                      />
                      {t("auth.otp_channel_whatsapp")}
                    </label>
                  </fieldset>
                </>
              )}
              {mode === "forgot_confirm" && (
                <>
                  <Field label={t("auth.otp_code")}>
                    <input
                      type="text"
                      required
                      dir="ltr"
                      maxLength={6}
                      inputMode="numeric"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      className="godo-login-input"
                    />
                  </Field>
                  <Field label={t("auth.new_password")}>
                    <input
                      type="password"
                      required
                      minLength={6}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="godo-login-input"
                    />
                  </Field>
                  <Field label={t("auth.password_confirm")}>
                    <input
                      type="password"
                      required
                      minLength={6}
                      value={newPasswordConfirm}
                      onChange={(e) => setNewPasswordConfirm(e.target.value)}
                      className="godo-login-input"
                    />
                  </Field>
                </>
              )}
            </>
          )}

          {error && (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
          {success && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              {success}
            </div>
          )}
          {devOtpHint && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" dir="ltr">
              {devOtpHint}
            </div>
          )}

          <button type="submit" disabled={submitting} className="godo-btn-primary">
            {submitting
              ? t("auth.submitting")
              : mode === "register"
                ? t("auth.submit_register")
                : mode === "forgot"
                  ? t("auth.submit_send_otp")
                  : mode === "forgot_confirm"
                    ? t("auth.submit_reset")
                    : t("auth.submit")}
          </button>
        </form>

        <div className="mt-6 space-y-3 text-center text-sm">
          {mode === "login" && (
            <>
              <button
                type="button"
                className="font-semibold text-godo-accent hover:underline"
                onClick={() => {
                  resetMessages();
                  setMode("register");
                }}
              >
                {t("auth.switch_register")}
              </button>
              <div>
                <button
                  type="button"
                  className="godo-btn-ghost w-full"
                  onClick={() => {
                    resetMessages();
                    setMode("forgot");
                  }}
                >
                  {t("auth.switch_forgot")}
                </button>
              </div>
            </>
          )}
          {mode !== "login" && (
            <button
              type="button"
              className="font-semibold text-godo-accent hover:underline"
              onClick={() => {
                resetMessages();
                setMode("login");
              }}
            >
              {t("auth.switch_login")}
            </button>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-godo-muted-green">
          {t("footer.standards")}
        </p>
      </div>

      <footer className="godo-login-footer" aria-label={t("auth.contact_footer")}>
        <a
          href="https://godo-erp.com"
          target="_blank"
          rel="noopener noreferrer"
          className="godo-contact-link godo-contact-link--web"
        >
          🌐 {t("auth.contact_website")}
        </a>
        <a
          href="https://wa.me/201008448095"
          target="_blank"
          rel="noopener noreferrer"
          className="godo-contact-link godo-contact-link--wa"
        >
          💬 {t("labels.contact_whatsapp")}
        </a>
      </footer>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="godo-login-field">
      <label>{label}</label>
      {children}
    </div>
  );
}
